import { useEffect, useState } from 'react'
import { Loader2, Mail, ArrowDownLeft, ArrowUpRight, Inbox, AlertTriangle } from 'lucide-react'
import { Badge } from '../../lib/shadcn/badge'
import { useGetContactEmails } from '../../hooks/backend/crm'
import { formatDateTime } from '../../utils/format'

interface EmailMessage {
  id: string
  subject: string
  from: string
  from_name: string
  to: string
  received_at: string
  preview: string
  body: string
  direction: 'inbound' | 'outbound'
  is_read: boolean
}

export default function ContactEmails({ email, name }: { email: string; name: string }) {
  const getContactEmails = useGetContactEmails()
  const [loading, setLoading] = useState(true)
  const [emails, setEmails] = useState<EmailMessage[]>([])
  const [error, setError] = useState<string | null>(null)
  const [openId, setOpenId] = useState<string | null>(null)

  useEffect(() => {
    if (!email) {
      setEmails([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    getContactEmails
      .trigger({ email, name }, { skipCache: true })
      .then((data: unknown) => setEmails((data as EmailMessage[] | undefined) ?? []))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load emails'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, name])

  if (!email) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        No email address on file for this contact.
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    )
  }

  if (error) {
    const authIssue = /access token|unauthor|invalidauthenticationtoken/i.test(error)
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Couldn&apos;t load emails from Microsoft.</p>
            {authIssue ? (
              <p className="mt-1">
                Connect your Microsoft account to the &quot;Microsoft Email&quot; resource (with the Mail.Read
                permission) and try again.
              </p>
            ) : (
              <p className="mt-1 break-words">{error}</p>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (emails.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        <Inbox className="w-5 h-5 mx-auto mb-2" />
        No emails found for {email}.
      </div>
    )
  }

  return (
    <div className="space-y-2">
      {emails.map(msg => {
        const isOpen = openId === msg.id
        const outbound = msg.direction === 'outbound'
        return (
          <button
            key={msg.id}
            onClick={() => setOpenId(isOpen ? null : msg.id)}
            className="w-full text-left rounded-md border border-border bg-card p-3 hover:bg-accent/40 transition-colors"
          >
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="secondary" className="gap-1">
                {outbound ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownLeft className="w-3 h-3" />}
                {outbound ? 'Sent' : 'Received'}
              </Badge>
              {!msg.is_read && !outbound && (
                <span className="inline-flex items-center gap-1 text-xs text-primary font-medium">
                  <Mail className="w-3 h-3" /> Unread
                </span>
              )}
              <span className="ml-auto text-xs text-muted-foreground">{formatDateTime(msg.received_at)}</span>
            </div>
            <div className="font-medium text-foreground">{msg.subject || '(no subject)'}</div>
            <div className="text-xs text-muted-foreground mb-1">
              {outbound ? `To: ${msg.to}` : `From: ${msg.from_name} <${msg.from}>`}
            </div>
            {isOpen ? (
              <p className="text-sm text-foreground whitespace-pre-wrap break-words mt-1">{msg.body}</p>
            ) : (
              <p className="text-sm text-muted-foreground truncate">{msg.preview}</p>
            )}
          </button>
        )
      })}
    </div>
  )
}
